import {
  auditTime,
  debounceTime,
  EMPTY,
  filter,
  map,
  merge,
  Observable,
  of,
  pipe,
  switchMap,
  tap,
} from 'rxjs';

import { connectivityService } from '@neptune/shared/core-connection-domain';

import { DocumentVisibility } from '../document-visibility';

/**
 * emits on:
 *   - query change, or
 *   - app become visible again, or
 *   - polling interval triggers
 * * but only when application is visible
 * * with event squashing (with given debounce time)
 * * emit query
 *
 * @param polling$ Observable that emits events in interval
 * @param manualHit$ Observable that emits events triggered by interactions
 * @param debounceTimeMs Number of milliseconds to debounce the events
 * @param weakTriggersThrottleTime Number of milliseconds from last trigger to react to
 *        weak trigger (like visibility change or connectivity change)
 * @returns Observable that emits events that should trigger polling refresh
 */
export function tempo<T>(
  polling$: Observable<unknown> = EMPTY,
  manualHit$: Observable<unknown> = EMPTY,
  debounceTimeMs?: number,
  weakTriggersThrottleTimeMs = 0,
) {
  const mapToThrottleTime = () => map(() => weakTriggersThrottleTimeMs);

  return pipe(
    switchMap((query: T) => {
      let lastEmitTimestamp = -Infinity;

      return merge(
        of(0),
        connectivityService.asObservable.pipe(mapToThrottleTime()),
        DocumentVisibility.fromVisibilityChange().pipe(mapToThrottleTime()),
        // When manually triggered, reset the last emit timestamp:
        // in case the manual trigger is blocked by filters, we want
        // to make sure that we emit immediately when the filters are gone.
        manualHit$.pipe(
          tap(() => (lastEmitTimestamp = -Infinity)),
          map(() => 0),
        ),
        polling$.pipe(map(() => 0)),
      ).pipe(
        filter(() => !DocumentVisibility.isAppHidden()),
        filter(() => !connectivityService.isLocked),
        // For some triggers we want to emit only if we were inactive for a while:
        // E.g. we only want to trigger refetch on visibilitychange if current
        // data may be stale, not every time the window/tab goes to the foreground.
        // Similarly, short connectivity issues should not trigger refetch.
        filter((value) => Date.now() - lastEmitTimestamp >= value),
        tap(() => {
          lastEmitTimestamp = Date.now();
        }),
        auditTime(0), // just to merge events in the same frame
        debounceTimeMs == null ? tap() : debounceTime(debounceTimeMs),
        map(() => query),
      );
    }),
  );
}
